import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CustomModal, ModalBody, ModalHeader } from "../../../../base/components/CustomModal";
import BiomarkerRange from "../../../../base/components/BiomarkerRange";
import { useLoading } from "../../../../base/hooks/useLoading";
import { useService } from "../../../../base/hooks/useService";
import { useDebounce } from "../../../../base/hooks/useDebounce";
import UserResultsService from "../../../../services/UserResults";
import { DEFAULT_DEBOUNCE_DELAY } from "../../../../base/constants/shared";
import Table from "../../../../base/components/Table";
import TableSearch from "../../../../base/components/Table/tableSearch";
import { useLocation } from "react-router-dom";
import { useHighlight } from "../../../../base/hooks/useHighlight";
import { useQueryString } from "../../../../base/hooks/useQueryString";
import { useLocationQuery } from "../../../../base/hooks/useQueryString";
import { useLocationSource, usePaginationProvider, useSearchProvider, useSortProvider, parseSorting } from "../../../../base/components/Table/hooks";
import ValueWithLabel from "../../Biomarkers/Details/ValueWIthLabel";
import { formatISODate } from "../../../../base/helpers/date";
import { powerFormat } from '../../../../base/hooks/usePowerUnitFormat';
import Icon from '../../../../base/components/Icon';
import joinClassNames from '../../../../base/helpers/joinClassNames';
import RoboGPService from "../../../../services/RoboGPService";
import { BUTTON_COLORS } from "../../../../base/components/Button/appearance";
import Button from "../../../../base/components/Button";
import BiomarkersService from "../../../../services/BiomarkersService";
import failedTestsRows from "./FailedTestsRows";
import { TUBE_LABELS } from '../../Biomarkers/CreateEdit/constants';
import RetestOrderModal from '../../../../base/components/RetestOrderModal/index';
import { CUSTOMER_RESULTS_CATEGORIES, RETEST_REQUEST_STATUSES } from "../constants";
import { isServiceEditable, isServiceVisible } from "../../../../base/helpers/adminRolePermissions";
import { ROLE_SECTION_SERVICE_NAMES, SIDEBAR_SERVICE_MAP } from "../../config";

const CUSTOMERS_RESULT_SERVICE_NAME = SIDEBAR_SERVICE_MAP["SIDEBAR.CUSTOMER_RESULTS"];

const BIOMARKER_RANGE_LABELS = [
  { label: "Untested", color: 'gray', icon: 'biomarkerGrayUntested' },
  { label: "Critical low", color: 'biomarker-range-red-text', icon: 'biomarkerRedAlert' },
  { label: "Low", color: 'biomarker-range-red-text', icon: 'biomarkerRedAlert' },
  { label: "Sub optimal", color: 'biomarker-range-yellow-text', icon: 'biomarkerYellowWarning' },
  { label: "Optimal", color: 'biomarker-range-green-text', icon: 'biomarkerGreenStatus' },
  { label: "Supra optimal", color: 'biomarker-range-yellow-text', icon: 'biomarkerYellowWarning' },
  { label: "High", color: 'biomarker-range-red-text', icon: 'biomarkerRedAlert' },
  { label: "Critical high", color: 'biomarker-range-red-text', icon: 'biomarkerRedAlert' },
]

const columns = [
  {
    Header: "#",
    width: 45,
    className: 'text-center align-middle',
    Cell: (cellProps) => {
      const { params: { offset = 0 } } = useLocationQuery();
      return <label className="mb-0">{cellProps.row.index + 1 + (offset ?? 0)}</label>;
    },
  },
  {
    Header: "Biomarker name",
    accessor: "biomarker.name",
    canSort: true,
    width: 250,
    Cell: ({ value, row: { original } }) => {
      const { params } = useLocationQuery();
      const { decorateText } = useHighlight(params.rangeSearch);
      const props = BIOMARKER_RANGE_LABELS[original.recommendationRange] || BIOMARKER_RANGE_LABELS[0];
      const displayValue = value || original?.biomarker?.name || '';
      return (
        <div className="w-100 text-truncate d-flex">
          <div className='d-flex align-items-center'>
            <Icon className={'me-2'} icon={props?.icon || 'circle'} />
          </div>
          <div>
            <p {...decorateText(displayValue)} className='biomarker-range-title mb-0' />
            <p className={`f-10px mb-0 tw-font-semibold ${props?.color}`}>{props?.label}</p>
          </div>
        </div>
      )
    }
  },
  {
    Header: "Tube",
    accessor: "biomarker.tube",
    width: 125,
    Cell: ({ row: { original } }) => {
      const raw = original?.biomarker?.tube;
      const value = typeof raw === "string" ? TUBE_LABELS[raw.toUpperCase()] || raw.slice(0,1).toUpperCase().concat(raw.slice(1)) : '-';
      return <div className="w-100 text-truncate">{value}</div>;
    },
  },
  {
    Header: "Range",
    accessor: "recommendationRange",
    canSort: true,
    width: 400,
    Cell: ({
      row: {
        original
      },
    }) => {
      if (original.isUntested) {
        return (
          <div className="w-100 text-muted tw-font-semibold">
            Result failed : {original.untestedReason}
          </div>
        );
      }

      return (
        <div className='mt-1'>
          <BiomarkerRange
            chosenResult={{ ...original, unit: original.unit }}
            isResultScreen={true}
            textColor={'red'}
            filter={original.filter || original.biomarker.filters[0]}
          />
        </div>
      )
    }
  },
  {
    Header: "Result",
    accessor: "value",
    headerClassName: "text-end",
    className: "text-end",
    width: 120,
    Cell: ({ value, row: { original } }) => {
      if (original.isUntested) {
        return <div className="w-100 text-truncate text-muted">-</div>;
      }
      return (
        <div className="w-100 text-truncate">{value}</div>
      )
    }
  },
  {
    Header: "Unit",
    accessor: "unit.unit",
    width: 125,
    Cell: ({ value, row: { original } }) => {
      const unitValue = value || original?.unit?.unit || '';
      if (original.value === null && original.isUntested) {
        return <div className="w-100 text-truncate text-muted">-</div>;
      }
      return (
        <div className="w-100 text-truncate">{powerFormat(unitValue)}</div>
      )
    }
  },
];

export default function ResultsPopupBlood({
  updateIsOpen,
  isOpen,
  row,
}) {
  /**
   * @type {UserResultsService}
   */
  const userResultsService = useService(UserResultsService);
  const roboGPService = useService(RoboGPService);
  const biomarkersService = useService(BiomarkersService);
  const locationSource = useLocationSource();
  const { search: locationSearch } = useLocation();

  const {
    params: {
      rangeSearch,
      marker,
      range
    }
  } = useQueryString(locationSearch)

  const [isLoading, { registerPromise }] = useLoading(true);
  const [userResults, updateUserResults] = useState([]);
  const [untestedResults, updateUntestedResults] = useState([]);
  const [userResultsPagination, updateUserResultsPagination] = useState({ totalCount: 0 });
  const [roboGPReport, updateRoboGPReport] = useState({});
  const hasPdf = useMemo(() => row?.pdfResultFile?.link, [row])
  const [isRetestModalOpen, setRetestModalOpen] = useState(false);
  const [retestRequestState, setRetestRequestState] = useState(row?.retestRequest || null);
  const isCustomersResultEditable = isServiceEditable(CUSTOMERS_RESULT_SERVICE_NAME);
  const isGpReportVisible = isServiceVisible(ROLE_SECTION_SERVICE_NAMES.GP_REPORT);



  const generateSortProviderParams = (name) => {
    const allParams = [
      "marker",
      "range"
    ];
    return {
      source: locationSource,
      alias: name,
      scope: "",
      onApplyClearScope: allParams.filter(paramName => paramName !== name)
    }
  }

  const filterByQuery = useCallback((i) => {
    const biomarkerName = i?.biomarker?.name || '';
    return biomarkerName.toLowerCase().startsWith(rangeSearch?.toLowerCase() || '');
  }, [rangeSearch])

  const { id, firstName, lastName, testProductName, labReceivedAt, category } = row;

  const onClose = () => updateIsOpen(false);

  const markerSortProvider = useSortProvider(generateSortProviderParams("marker"));
  const valSortProvider = useSortProvider(generateSortProviderParams("range"));

  const getResultsList = useCallback(() => {
    const sortObject = parseSorting({
      marker,
      range
    });

    const ROBOGP_STATUS_ACCEPTED = 5;
    const shouldFetchRoboGP = isGpReportVisible && row.roboGPReportId && row.roboGPReportStatus === ROBOGP_STATUS_ACCEPTED;

    registerPromise(
      Promise.all([
        userResultsService.getResultsList(id, { ...sortObject }),
        row.failedTests ? failedTestsRows(row.failedTests, biomarkersService) : Promise.resolve([]),
        shouldFetchRoboGP ? roboGPService.getReportItem(row.roboGPReportId, { userId: row.userId }) : Promise.resolve(null)
      ])
        .then(([resultsData, untestedRows, roboGPReportData]) => {
          const { data, pagination } = resultsData;
          const dataWithCategory = data.map(i => ({ ...i, category }));
          updateUserResults(dataWithCategory);
          updateUntestedResults(untestedRows);
          updateUserResultsPagination(pagination);
          const report = roboGPReportData?.data ?? roboGPReportData;
          if (report) {
            updateRoboGPReport(report);
          }
        })
        .catch(() => { })
    );

  }, [row, id, marker, range, category, userResultsService, biomarkersService, roboGPService, isGpReportVisible]);


  const paginationProvider = usePaginationProvider({
    source: locationSource,
    alias: "offset",
    scope: "",
    fallback: 0
  });

  const searchProvider = useSearchProvider({
    source: locationSource,
    scope: "",
    alias: 'rangeSearch',
    onApplyClearScope: ["rangeSearch"]
  });

  const downloadFile = useCallback(
    () => {
      if (hasPdf) {
        window.open(hasPdf, '_blank').focus()
      }
    },
    [hasPdf],
  )

  useEffect(() => {
    getResultsList()
  }, [getResultsList]);

  useEffect(() => {
    setRetestRequestState(row?.retestRequest || null);
  }, [row?.retestRequest, row?.id]);

  return (
    <CustomModal isOpen={isOpen} className="hl7-modal" toggle={onClose}>
      <ModalHeader onClose={onClose} className="text-dark">
        {firstName} {lastName}
      </ModalHeader>

      <ModalBody className="pb-0">
        <section className="d-flex align-items-center mb-3">
          <ValueWithLabel
            value={testProductName || "-"}
            label="Product name"
            className="me-4"
          />
          <ValueWithLabel
            value={formatISODate(labReceivedAt)}
            label="Test date"
          />

          <section className="d-flex flex-row flex-grow-1 justify-content-end">
            <Button
              className={joinClassNames(" btn btn-primary me-2", hasPdf && 'cursor-pointer', !hasPdf && 'opacity-30')}
              onClick={hasPdf ? downloadFile : undefined}
            >
              <div className='d-flex flex-row flex-grow-1 '> <Icon className="me-2" icon='downloadPDF' /> <span>Download file</span></div>

            </Button>
            {isGpReportVisible && roboGPReport?.report?.file?.link ? (
              <>
                <Button
                  className="btn btn-primary me-2 cursor-pointer"
                  onClick={() => {
                    window.open(
                      roboGPReport?.report?.file?.link,
                      '_blank'
                    );
                  }}>
                  <Icon className="me-2" icon='downloadRoboGP' />
                  Download GP Report
                </Button>
              </>
            ) : null}
            {/* Show create retest order button when there is no retest order already created, there are some untested results, retest request is not cancelled and category is blood */}
            {isCustomersResultEditable && (!retestRequestState || retestRequestState.status === RETEST_REQUEST_STATUSES.CANCELLED) && untestedResults.length > 0 && category === CUSTOMER_RESULTS_CATEGORIES.BLOOD_CATEGORY ? (
              <>
                <Button
                  color={BUTTON_COLORS.primary}
                  className='btn btn-primary me-2 cursor-pointer'
                  onClick={() => setRetestModalOpen(true)}
                >
                  Create Retest Order
                </Button>
                <RetestOrderModal
                  isOpen={isRetestModalOpen}
                  onClose={(status) => {
                    setRetestModalOpen(false);
                    if (status === "pending") {
                      setRetestRequestState({ status: RETEST_REQUEST_STATUSES.PENDING });
                      getResultsList();
                    }
                  }}
                  result={{ ...row, retestRequest: retestRequestState }}
                />
              </>
            ) : null}
          </section>
        </section>
        <section className="mb-3">
          <div className='ms-auto d-flex align-items-center'>
            <TableSearch
              className="biomarkers-search me-3"
              search={searchProvider.getValue()}
              onSearch={searchProvider.setValue}
            />
          </div>
        </section>

        <Table
          columns={columns}
          data={[...untestedResults, ...userResults].filter(filterByQuery)}
          isAddNewBiomarker
          loading={isLoading}
          totalCount={userResultsPagination.totalCount}
          limit={100}
          offset={0}

          sortProviders={{
            ['biomarker.name']: markerSortProvider,
            ['recommendationRange']: valSortProvider,
          }}

          paginationProvider={paginationProvider}
          withoutPagination
          commonPlaceholder="No customer results for now."
          hasActiveFilters={false}
          tableClassName="popup-table"
        />
      </ModalBody>
    </CustomModal>
  )
}

